import { useEffect, useState } from 'react';
import type { MouseEvent } from 'react';

interface JumpItem {
  id: string;
  label: string;
  icon: string;
}

const items: JumpItem[] = [
  { id: 'market-read', label: 'Market Read', icon: 'ri-line-chart-line' },
  { id: 'entry-conditions', label: 'Entry Conditions', icon: 'ri-filter-3-line' },
  { id: 'trade-management', label: 'Trade Management', icon: 'ri-shield-check-line' },
  { id: 'trading-session', label: 'Sessions', icon: 'ri-time-line' },
];

function StrategyQuickNav() {
  const [active, setActive] = useState<string>(items[0].id);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const onScroll = () => {
      const scrollable = document.documentElement.scrollHeight - window.innerHeight;
      const ratio = scrollable > 0 ? window.scrollY / scrollable : 0;
      setProgress(Math.min(1, Math.max(0, ratio)));
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, []);

  useEffect(() => {
    const sections = items
      .map((item) => document.getElementById(item.id))
      .filter((el): el is HTMLElement => Boolean(el));
    if (sections.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActive(visible[0].target.id);
      },
      { rootMargin: '-124px 0px -55% 0px', threshold: 0 },
    );

    sections.forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, []);

  const handleJump = (event: MouseEvent<HTMLAnchorElement>, id: string) => {
    event.preventDefault();
    const el = document.getElementById(id);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setActive(id);
    window.history.replaceState(null, '', `#${id}`);
  };

  const handleTop = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    window.history.replaceState(null, '', '#top');
  };

  return (
    <div className="sticky top-[76px] z-40 w-full relative border-b border-background-300 bg-background-50/90 backdrop-blur-md">
      <div className="max-w-[1440px] mx-auto px-4 md:px-8 lg:px-12">
        <div className="flex items-center gap-3 h-11">
          <span className="hidden md:inline-flex items-center gap-1.5 shrink-0">
            <span className="w-1 h-1 rounded-full bg-primary-500"></span>
            <span className="text-[10px] font-data tracking-[0.18em] text-foreground-400">
              ON THIS PAGE
            </span>
          </span>

          <nav className="flex-1 min-w-0 flex items-center gap-1 overflow-x-auto py-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {items.map((item) => {
              const isActive = item.id === active;
              return (
                <a
                  key={item.id}
                  href={`#${item.id}`}
                  onClick={(event) => handleJump(event, item.id)}
                  aria-current={isActive ? 'true' : undefined}
                  className={`inline-flex items-center gap-1.5 h-7 px-2.5 md:px-3 rounded-full text-[12px] font-medium whitespace-nowrap transition-colors duration-200 cursor-pointer border ${
                    isActive
                      ? 'bg-primary-500 border-primary-500 text-background-50'
                      : 'border-transparent text-foreground-600 hover:text-foreground-950 hover:bg-background-100'
                  }`}
                >
                  <span className="w-3.5 h-3.5 flex items-center justify-center">
                    <i className={`${item.icon} text-[13px]`}></i>
                  </span>
                  {item.label}
                </a>
              );
            })}
          </nav>

          <a
            href="#top"
            onClick={handleTop}
            aria-label="Back to top"
            className="shrink-0 inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full border border-background-300 text-foreground-600 hover:text-primary-700 hover:border-primary-300 hover:bg-primary-50/60 transition-colors duration-200 cursor-pointer"
          >
            <span className="w-3.5 h-3.5 flex items-center justify-center">
              <i className="ri-arrow-up-line text-[13px]"></i>
            </span>
            <span className="hidden sm:inline text-[11px] font-data tracking-wide">TOP</span>
          </a>
        </div>
      </div>

      <span className="absolute bottom-0 left-0 right-0 h-[2px] bg-background-200/70" aria-hidden="true">
        <span
          className="block h-full bg-primary-500 transition-[width] duration-150 ease-out"
          style={{ width: `${progress * 100}%` }}
        ></span>
      </span>
    </div>
  );
}

export default StrategyQuickNav;